<script setup lang="ts">
import { computed } from 'vue';
import { type Product, formatEuros, formatAgeRating } from '../../api/catalog';

const props = defineProps<{ product: Product }>();

const inStock = computed(() => (props.product.totalQty ?? 0) > 0 && props.product.available !== false);
const ageBadge = computed(() => formatAgeRating(props.product.ageRating));

// Bottom tag chips: platform first, then genres — capped to keep the row tidy.
const tags = computed(() => {
  const out: string[] = [];
  if (props.product.platform) out.push(props.product.platform);
  out.push(...(props.product.genres ?? []));
  return out.slice(0, 3);
});
</script>

<template>
  <article class="group flex h-full flex-col gap-2 rounded-xl border border-accent bg-surface p-2.5">
    <div class="relative overflow-hidden rounded-lg bg-surface-2">
      <img
        v-if="product.coverUrl"
        :src="product.coverUrl"
        :alt="product.name"
        loading="lazy"
        class="aspect-[4/5] w-full object-cover transition duration-500"
        :class="inStock ? 'group-hover:scale-105' : 'opacity-40 grayscale'"
      />
      <div v-else class="grid aspect-[4/5] w-full place-items-center font-display text-xs uppercase text-muted">{{ $t('common.noImage') }}</div>

      <!-- Age rating -->
      <span
        v-if="ageBadge"
        class="absolute left-2 top-2 rounded-md bg-black/80 px-1.5 py-0.5 font-display text-[10px] text-white backdrop-blur-sm"
      >{{ ageBadge }}</span>

      <!-- Edition badge: DLC (accent) when the title is a DLC, else base game -->
      <span
        v-if="product.isDlc"
        class="absolute bottom-2 left-2 rounded-full bg-accent px-2.5 py-0.5 font-display text-[10px] uppercase text-white"
      >DLC</span>
      <span
        v-else
        class="absolute bottom-2 left-2 rounded-full border border-accent bg-black px-2.5 py-0.5 font-display text-[10px] uppercase text-white"
      >{{ $t('common.baseGame') }}</span>

      <!-- Out of stock overlay -->
      <span
        v-if="!inStock"
        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full border border-white/70 px-5 py-1.5 font-display text-xs uppercase tracking-wide text-white"
      >{{ $t('catalog.outOfStock') }}</span>
    </div>

    <h3 class="line-clamp-1 font-display text-sm uppercase leading-tight" :class="inStock ? 'text-white' : 'text-muted'">
      {{ product.name }}
    </h3>

    <p class="text-sm" :class="inStock ? 'text-ink' : 'text-muted/60'">
      {{ $t('common.startingAt') }}
      <span :class="inStock ? 'font-semibold text-accent' : 'text-muted'">{{ formatEuros(product.ourPriceCents) }}</span>
    </p>

    <div v-if="tags.length" class="mt-auto flex flex-wrap gap-1.5 pt-0.5">
      <span
        v-for="tag in tags"
        :key="tag"
        class="rounded-full border border-accent px-2 py-0.5 text-[10px] font-medium text-white"
      >{{ tag }}</span>
    </div>
  </article>
</template>
